import { LitElement, html, css } from 'lit'
import { consume } from '@lit/context'
import { breakpointContext, isSubMenuOpenContext, navigationContext } from '../context.ts'
import { customElement, property } from 'lit/decorators.js'
import { TW, type Breakpoint, type MenuItem } from '../../../shared'

const TwLitElement = TW(LitElement)

/**
 * Sample Lit Element in assets/lit
 */
@customElement('desktop-navigation')
export class DesktopNavigation extends TwLitElement {

    @property({ type: String, attribute: 'additional-links' })
    additionalLinks?: string

    @consume({context: navigationContext, subscribe: true})
    private _data: MenuItem[] = []

    @consume({context: breakpointContext, subscribe: true}) 
    private _breakpoint: Breakpoint | null = null

    @consume({context: isSubMenuOpenContext, subscribe: true})
    private _isSubMenuOpen: boolean = false

    static styles = css`
        :host([hidden]) {
            display: none;
        }
    `

    private _isShown() 
    {
        return this._isSubMenuOpen 
            && [ 'xl', '2xl', '3xl' ].includes( this._breakpoint?.name ?? '' ) // Wann soll die Navigation angezeigt werden?
    }

    render() 
    {
        if ( ! this._isShown() ) {
            this.setAttribute( 'hidden', '' );
            return html``
        }

        this.removeAttribute( 'hidden' );

        // Desktop Navigation: 
        // - Falls kein MenuItem aktiv ist, wird Level 0 der Navigation angezeigt
        // - Falls ein oder mehrere MenuItem aktiv sind, werden pro Ebene alle Elemente der Ebene aufgelistet

        const activeItems = this._data.filter( item => item.active ).sort( (a: MenuItem, b: MenuItem) => a.level - b.level )

        let menu = html``

        if ( activeItems.length < 1 ) {

            // Kein Aktives MenuItem => Level 0 der Navigation anzeigen

            menu = html`
                <ul>
                    ${ this._data.filter( item => item.level === 0 ).map( 
                        item => html`<li>
                            <menu-item 
                                id=${item.id}
                                class-default=${"font-medium text-neutral-light pb-2 border-b-2 border-transparent hover:text-primary hover:border-b-primary"}
                                class-active=${"font-medium text-black pb-2 border-b-2 border-transparent"}
                            ></menu-item>
                        </li>`
                    ) }
                </ul>
            `
        } else {

            // Mindestens ein aktives MenuItem => Wir gehen den aktiven Pfad durch
            // und zeigen je Ebene einmal die Geschwister-Liste, daneben (falls vorhanden)
            // die Children der jeweils aktiven Ebene an.

            const columns: MenuItem[][] = []

            const rootActive = activeItems[0]

            if ( rootActive ) {

                // Spalte 1: Geschwister des obersten aktiven Elements (typisch Level 0)
                const rootSiblings = this._data.filter( menuItem => menuItem.parentId === rootActive.parentId )
                columns.push( rootSiblings )

                // Weitere Spalten: jeweils Children des aktiven Pfads (beliebige Tiefe)
                let current: MenuItem | undefined = rootActive

                while ( current ) {

                    const children = this._data.filter( menuItem => menuItem.parentId === current!.id )

                    if ( children.length < 1 ) {
                        break
                    }

                    columns.push( children )

                    // Nächstes aktives Element entlang des Pfads (falls vorhanden)
                    current = activeItems.find( item => item.parentId === current!.id )
                }
            }

            menu = html`<div class="flex gap-8">
                ${ columns.map( (columnItems, columnIndex) => html`
                    <ul class=${ columnIndex > 0 ? "border-l border-l-[rgb(187,187,187)] pl-8" : "" }>
                        ${ columnItems.map( item => html`
                            <li>
                                <menu-item 
                                    id=${item.id} 
                                    class-default=${"font-medium text-neutral-light pb-2 border-b-2 border-transparent hover:text-primary hover:border-b-primary"}
                                    class-active=${"font-medium text-black pb-2 border-b-2 border-transparent"}
                                ></menu-item>
                            </li>
                        ` ) }
                    </ul>
                ` ) }
            </div>`

        }

        return html`<nav class="py-6 container" aria-label="Hauptmenü">
            <div class="alignwide flex justify-between pt-6">
                ${menu}
                <additional-links data=${this.additionalLinks}></additional-links>
            </div>
        </nav>`

    }
}

declare global {
    interface HTMLElementTagNameMap {
        'desktop-navigation': DesktopNavigation
    }
}
